import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  X, 
  Shield, 
  CheckCircle2,
  Loader2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailPopup({ onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source: 'popup' });
      setSuccess(true);
      toast.success('Check your email for the Privacy Checklist!');
      
      // Store in localStorage to not show again
      localStorage.setItem('privacyshield_subscribed', 'true');
      
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="email-popup">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-2xl p-8 max-w-md w-full animate-slide-up">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
          data-testid="popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#00FF94]" />
            </div>
            <h3 className="font-heading font-bold text-xl text-white mb-2">
              You're In!
            </h3>
            <p className="text-gray-400 text-sm">
              Check your inbox for the Free Privacy Checklist.
            </p>
          </div>
        ) : (
          <>
            {/* Icon */}
            <div className="w-16 h-16 mx-auto rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-6">
              <FileText className="w-8 h-8 text-[#00FF94]" />
            </div>

            {/* Content */}
            <div className="text-center mb-6">
              <h3 className="font-heading font-bold text-2xl text-white mb-2">
                Free Privacy Checklist
              </h3>
              <p className="text-gray-400">
                Get our 10-step checklist to protect yourself from surveillance tools like Fog Reveal.
              </p>
            </div>

            {/* Benefits */}
            <ul className="space-y-2 mb-6">
              {[
                'Disable location tracking step-by-step',
                'Best VPN recommendations',
                'Browser privacy settings guide',
                'Weekly privacy tips & news'
              ].map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-[#00FF94] flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-[#171717] border-[#262626] text-white placeholder:text-gray-600 h-12"
                required
                data-testid="popup-email"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn-primary h-12 text-base"
                data-testid="popup-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Get Free Checklist'
                )}
              </Button>
            </form>

            <p className="text-xs text-gray-600 text-center mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
